import * as React from "react";
import QRCode from "react-qr-code";

import { ARTIFACT_DISPLAY_HEIGHT_PIXELS, useArtifactColors } from "components/reports/passports/utils/artifactShared";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { resolveColor } from "utils/commonFunctions";

const PRINTING_ARTIFACT_CLASS = "printing-passport-artifact";

const PNG_WIDTH_PIXELS = 1_024;

const CSS_VARIABLE_PATTERN = /var\((--[\w-]+)(?:,[^)]*)?\)/g;
const COLOR_ATTRIBUTES = ["fill", "stroke", "style"];

const findArtifactSvg = (container: HTMLElement | null): SVGSVGElement | undefined => {
    const svg = container?.querySelector(":scope > svg");
    return svg instanceof SVGSVGElement ? svg : undefined;
};

// The theme's colours are CSS variables that don't resolve when the SVG is serialized outside the page.
const resolveCssVariables = (value: string): string =>
    value.replace(CSS_VARIABLE_PATTERN, (variable, name: string) => resolveColor(name) || variable);

const resolveSvgColors = (svg: SVGSVGElement) => {
    [svg, ...svg.querySelectorAll("*")].forEach((element) =>
        COLOR_ATTRIBUTES.forEach((attribute) => {
            const value = element.getAttribute(attribute);
            if (value?.includes("var(")) {
                element.setAttribute(attribute, resolveCssVariables(value));
            }
        })
    );
};

const downloadBlob = (blob: Blob, fileName: string) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
};

// The SVG stays scalable, because the label printers and their software differ in width.
export const downloadArtifactSvg = (container: HTMLElement | null, fileName: string) => {
    const clone = findArtifactSvg(container)?.cloneNode(true);
    if (!(clone instanceof SVGSVGElement)) {
        return;
    }
    resolveSvgColors(clone);
    downloadBlob(
        new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml;charset=utf-8" }),
        `${fileName}.svg`
    );
};

export const downloadArtifactPng = (container: HTMLElement | null, fileName: string, backgroundColor: string) => {
    const svg = findArtifactSvg(container);
    if (!svg) {
        return;
    }
    const viewBox = svg.viewBox.baseVal;
    const widthPixels = PNG_WIDTH_PIXELS;
    const heightPixels = Math.round((widthPixels * viewBox.height) / viewBox.width);
    const clone = svg.cloneNode(true);
    if (!(clone instanceof SVGSVGElement)) {
        return;
    }
    resolveSvgColors(clone);
    clone.setAttribute("width", String(widthPixels));
    clone.setAttribute("height", String(heightPixels));
    // A data URL, because the content security policy blocks images from blob URLs.
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
    const image = new Image();
    image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = widthPixels;
        canvas.height = heightPixels;
        const context = canvas.getContext("2d");
        if (context) {
            context.fillStyle = resolveCssVariables(backgroundColor);
            context.fillRect(0, 0, widthPixels, heightPixels);
            context.drawImage(image, 0, 0, widthPixels, heightPixels);
            canvas.toBlob((blob) => {
                if (blob) {
                    downloadBlob(blob, `${fileName}.png`);
                }
            }, "image/png");
        }
    };
    image.src = url;
};

export const printArtifact = () => {
    usageStatisticsService.sendEvent({
        category: Category.DEVICE_PASSPORT,
        action: Action.PRINT_DEVICE_PASSPORT_ARTIFACT,
    });
    document.body.classList.add(PRINTING_ARTIFACT_CLASS);
    window.addEventListener("afterprint", () => document.body.classList.remove(PRINTING_ARTIFACT_CLASS), {
        once: true,
    });
    window.print();
};

const QR_PADDING = 24;
const QR_SIZE = ARTIFACT_DISPLAY_HEIGHT_PIXELS - 2 * QR_PADDING;

export const PassportQrCodeTab = (props: { shareUrl: string }) => {
    const colors = useArtifactColors();
    const size = QR_SIZE + 2 * QR_PADDING;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect
                x="0.5"
                y="0.5"
                width={size - 1}
                height={size - 1}
                rx="7.5"
                fill="var(--theme-icon-fill-white)"
                stroke={colors.border}
            />
            <g transform={`translate(${QR_PADDING} ${QR_PADDING})`}>
                <QRCode value={props.shareUrl} size={QR_SIZE} />
            </g>
        </svg>
    );
};
